// The dispatch board's week (dispatch parity 6): each technician's days, Monday to Sunday, as
// "3 visits · 6h", with the days they're off or away; a day opens that day's board. One read of
// dispatch.board for the week, re-read on the same live changes as the day's board.

import { useCallback, useEffect, useRef, useState } from 'react';
import { RpcError } from '@flowology/client';
import { localDateOf } from '@flowology/kernel';
import { client } from './api.ts';
import { liveStream } from './connection/live.ts';
import { scrollFocus } from './shared/scrollFocus.ts';
import { BOARD_EVENTS, daysFrom, weekCells, weekCellText, weekOf, type Board } from './board-model.ts';

const dayHead = (date: string) =>
  new Intl.DateTimeFormat('en-CA', { weekday: 'short', day: 'numeric', timeZone: 'UTC' }).format(
    new Date(`${date}T12:00:00Z`),
  );
const dayLong = (date: string) =>
  new Intl.DateTimeFormat('en-CA', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${date}T12:00:00Z`));

export function WeekView({
  date,
  today,
  onPick,
}: {
  /** Any day of the week shown. */
  date: string;
  today: string;
  /** A day's cell: that day's board. */
  onPick: (date: string) => void;
}) {
  const [from, to] = weekOf(date);
  const [week, setWeek] = useState<Board | null>(null);
  // BL-057 phase 2: each tech's free minutes by day, from the same week's capacity read
  const [open, setOpen] = useState<Map<string, Map<string, number>>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      const [b, c] = await Promise.all([
        client.call<Board>('dispatch.board', { from, to }),
        client
          .call<{ days: { date: string; techOpenMinutes?: { id: string; openMinutes: number }[] }[] }>(
            'capacity.availability',
            { from, to },
          )
          .catch(() => null), // without it, the week is as it was
      ]);
      const byTech = new Map<string, Map<string, number>>();
      for (const d of c?.days ?? [])
        for (const t of d.techOpenMinutes ?? []) {
          const m = byTech.get(t.id) ?? new Map<string, number>();
          m.set(d.date, t.openMinutes);
          byTech.set(t.id, m);
        }
      setOpen(byTech);
      setWeek(b);
      setError(null);
    } catch (e) {
      setError(e instanceof RpcError ? e.message : 'Could not load the week.');
    }
  }, [from, to]);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    const es = liveStream();
    let timer: ReturnType<typeof setTimeout> | undefined;
    es.addEventListener('change', (e) => {
      const { type } = JSON.parse((e as MessageEvent<string>).data) as { type: string };
      if (!BOARD_EVENTS.test(type)) return;
      clearTimeout(timer);
      timer = setTimeout(() => void load(), 250);
    });
    return () => {
      clearTimeout(timer);
      es.close();
    };
  }, [load]);

  const days = daysFrom(from, to);
  const shown = week && week.date === from ? week : null;
  const tz = shown?.timeZone ?? 'UTC';
  // on a phone the week scrolls sideways: bring today's column in, beside the names
  const box = useRef<HTMLElement>(null);
  const scrolledFor = useRef<string | null>(null);
  useEffect(() => {
    const wrap = box.current?.querySelector<HTMLElement>('.table-wrap');
    const th = wrap?.querySelector<HTMLElement>('th.today');
    if (!shown || !wrap || scrolledFor.current === shown.date) return;
    scrolledFor.current = shown.date;
    if (!th || wrap.scrollWidth <= wrap.clientWidth) return;
    const names = wrap.querySelector<HTMLElement>('thead th')?.offsetWidth ?? 0;
    wrap.scrollLeft = Math.max(0, th.offsetLeft - names);
  });
  return (
    <main className="board week-board" aria-busy={!shown && !error}>
      <section className="week" aria-label="The week" ref={box}>
        {!shown ? (
          <p className="muted">{error ?? 'Getting the week…'}</p>
        ) : (
          <div className="table-wrap" ref={scrollFocus}>
            <table className="grid-table week-table" aria-label="The week by technician">
              <thead>
                <tr>
                  <th scope="col">Technician</th>
                  {days.map((d) => (
                    <th key={d} scope="col" className={d === today ? 'today' : undefined}>
                      {dayHead(d)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.technicians.map((t) => (
                  <tr key={t.id}>
                    <th scope="row">{t.name}</th>
                    {weekCells(t.visits, days, t.days, tz, open.get(t.id)).map((c) => {
                      const text = weekCellText(c);
                      return (
                        <td key={c.date} className={c.date === today ? 'today' : undefined}>
                          <button
                            className={`week-cell${c.visits ? '' : ' quiet'}${c.off || c.away ? ' away' : ''}`}
                            onClick={() => onPick(c.date)}
                            aria-label={`${t.name}, ${dayLong(c.date)}: ${text}`}
                          >
                            {text}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr className="waiting">
                  <th scope="row">Waiting</th>
                  {days.map((d) => {
                    const n = shown.unassigned.filter((v) => localDateOf(v.start, tz) === d).length;
                    return (
                      <td key={d} className={d === today ? 'today' : undefined}>
                        {n ? (
                          <button
                            className="week-cell"
                            onClick={() => onPick(d)}
                            aria-label={`${dayLong(d)}: ${n} waiting for a technician`}
                          >
                            {n} waiting
                          </button>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}
